/**
 * Ziele samt berechnetem Fortschritt.
 *
 * Wie bei den Gewohnheiten wird nichts gespeichert, was sich ausrechnen laesst:
 * Fortschritt, Zeitanteil und Tempo entstehen bei jedem Rendern neu. Ein
 * gespeicherter Fortschritt driftet spaetestens dann, wenn sich ein Key Result
 * aendert.
 */
import { useLiveQuery } from 'dexie-react-hooks';
import { useMemo } from 'react';

import { db } from '@/db/schema';
import { HORIZON_ORDER } from '@/db/repositories/goals';
import { daysRemaining, periodProgress, type Period } from '@/domain/goals/period';
import { goalProgress, paceStatus, type PaceStatus } from '@/domain/goals/progress';
import { useAreaFilter } from '@/store/areaFilter';
import { today } from '@/lib/datetime';
import type { Goal, ID } from '@/domain/types';

export interface GoalWithProgress {
  goal: Goal;
  period: Period;
  /** Anteil der erreichten Key Results, 0..1. */
  progress: number;
  /** Anteil der verstrichenen Zeit, 0..1. */
  timeProgress: number;
  pace: PaceStatus;
  daysLeft: number;
}

export function decorate(goal: Goal, heute: string): GoalWithProgress {
  const period = { start: goal.periodStart, end: goal.periodEnd };
  const progress = goalProgress(goal);
  const timeProgress = periodProgress(period, heute);

  return {
    goal,
    period,
    progress,
    timeProgress,
    pace: paceStatus(progress, timeProgress),
    daysLeft: daysRemaining(period, heute),
  };
}

/** Alle Ziele, nach Bereichsfilter, mit Kindzielen unter ihrem Elternziel. */
export function useGoals(): {
  top: GoalWithProgress[];
  childrenOf: Map<ID, GoalWithProgress[]>;
  total: number;
} {
  const heute = today();
  const { activeAreaIds } = useAreaFilter();

  const alle = useLiveQuery(() => db.goals.toArray(), [], [] as Goal[]);

  return useMemo(() => {
    const sichtbar = alle
      .filter((g) => activeAreaIds.length === 0
        || (g.areaId != null && activeAreaIds.includes(g.areaId)))
      .sort(
        (a, b) =>
          HORIZON_ORDER.indexOf(a.horizon) - HORIZON_ORDER.indexOf(b.horizon)
          || a.periodStart.localeCompare(b.periodStart)
          || a.title.localeCompare(b.title, 'de'),
      )
      .map((g) => decorate(g, heute));

    const ids = new Set(sichtbar.map((g) => g.goal.id));
    const childrenOf = new Map<ID, GoalWithProgress[]>();

    for (const eintrag of sichtbar) {
      const parent = eintrag.goal.parentGoalId;
      // Zeigt der Elternverweis ins Leere (gefiltert oder geloescht), steht das
      // Ziel fuer sich - es darf nicht unsichtbar werden.
      if (!parent || !ids.has(parent)) continue;
      const liste = childrenOf.get(parent) ?? [];
      liste.push(eintrag);
      childrenOf.set(parent, liste);
    }

    const top = sichtbar.filter(
      (e) => !e.goal.parentGoalId || !ids.has(e.goal.parentGoalId),
    );

    return { top, childrenOf, total: sichtbar.length };
  }, [alle, activeAreaIds, heute]);
}

/** Laufende Ziele fuer die Startseite - unabhaengig vom Bereichsfilter. */
export function useActiveGoals(limit = 5): GoalWithProgress[] {
  const heute = today();
  const alle = useLiveQuery(() => db.goals.toArray(), [], [] as Goal[]);

  return useMemo(
    () => alle
      .filter((g) => (g.status === 'offen' || g.status === 'laeuft')
        && heute >= g.periodStart && heute <= g.periodEnd)
      .map((g) => decorate(g, heute))
      // Das Dringendste zuerst: wenig Zeit und viel Rueckstand.
      .sort((a, b) => (a.progress - a.timeProgress) - (b.progress - b.timeProgress))
      .slice(0, limit),
    [alle, heute, limit],
  );
}
